import { QueryState as SharedQueryState, color, type QueryStateProps } from '@central/ui';
import { Button, Table, VisuallyHidden } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';

/** "Customers / Riverside County DOT / Production"; the last item is plain text. */
export function Crumbs({
  items,
}: {
  items: Array<{ label: string; to?: string; params?: object }>;
}) {
  return (
    <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: color.textMuted }}>
      {items.map((it, i) => (
        <span key={i}>
          {i > 0 && ' / '}
          {it.to ? (
            <Link to={it.to} params={it.params as never} style={{ color: color.link }}>
              {it.label}
            </Link>
          ) : (
            it.label
          )}
        </span>
      ))}
    </nav>
  );
}

/** Table that scrolls sideways inside its card on narrow screens. */
export function DataTable({
  head,
  children,
  minWidth = 640,
  label,
}: {
  head: ReactNode[];
  children: ReactNode;
  minWidth?: number;
  label?: string;
}) {
  return (
    <Table.ScrollContainer minWidth={minWidth}>
      <Table verticalSpacing="sm" horizontalSpacing="md" fz={14} aria-label={label}>
        <Table.Thead>
          <Table.Tr>
            {head.map((h, i) => (
              <Table.Th
                key={i}
                style={{ fontSize: 12, color: color.textMuted, whiteSpace: 'nowrap' }}
              >
                {h}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** QueryState with the console's error text. */
export function QueryState(p: Omit<QueryStateProps, 'describeError' | 'title'>) {
  return <SharedQueryState {...p} describeError={errorMessage} />;
}

/** "Load more" under a cursor-paged list; what names the list for screen readers. */
export function LoadMore({
  what,
  loading,
  onClick,
}: {
  what?: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <div style={{ padding: '12px 20px' }}>
      <Button variant="default" size="sm" loading={loading} onClick={onClick}>
        Load more{what && <VisuallyHidden> {what}</VisuallyHidden>}
      </Button>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p style={{ margin: 0, padding: '12px 20px', fontSize: 14, ...muted }}>{children}</p>;
}
